/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";

/**
 * The load / loading / error state machine the drive list pages all repeat.
 *
 * Only that machine is shared. Their layouts genuinely differ — Favorites has
 * no table, Shared has two — so this deliberately returns state rather than
 * rendering anything. Forcing one config-driven table over four different
 * screens would cost more than the duplication it removed.
 *
 * `empty` is the shape to fall back to on failure, so a page can keep
 * destructuring the same keys instead of guarding every render.
 *
 * `fetcher` must be stable (useCallback / module-scope), or this re-fires on
 * every render.
 */
export const useAsyncData = (fetcher, empty, errorMessage) => {
  const [state, setState] = useState({ loading: true, error: null, ...empty });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      setState({ loading: false, error: null, ...(await fetcher()) });
    } catch (err) {
      setState({ loading: false, error: err.message || errorMessage, ...empty });
    }
    // `empty` is a literal at each call site, so depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, errorMessage]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load, setState };
};
